// src/pages/InitializeData.js
import React, { useState } from "react";
import { initializeFirestore } from "../utils/initializeFirestore";
import { Database, CheckCircle, XCircle, Loader } from "lucide-react";

const InitializeData = () => {
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");

  const handleInitialize = async () => {
    setStatus("loading");
    setMessage("Inicializando estructura de datos...");

    try {
      const result = await initializeFirestore();
      if (result) {
        setStatus("success");
        setMessage(
          "¡Estructura de Firestore inicializada correctamente! Ya puedes usar el panel de administración.",
        );
      } else {
        setStatus("error");
        setMessage("Hubo un problema al inicializar. Revisa la consola.");
      }
    } catch (error) {
      setStatus("error");
      setMessage(`Error: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4">
            <Database className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Inicializar Base de Datos
          </h1>
          <p className="text-blue-100">
            Configura la estructura inicial de tu portafolio
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              ¿Qué hace esta herramienta?
            </h2>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Crea la colección "content" con datos de Hero y About</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Crea la colección "projects" con 4 proyectos de ejemplo</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>
                  Usa tus imágenes actuales de Firebase Storage
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Permite que el panel de admin funcione correctamente</span>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              <strong>Importante:</strong> Solo necesitas hacer esto una vez.
              Después podrás editar todo desde el panel de administración.
            </p>
          </div>

          {/* Status Message */}
          {status !== "idle" && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${
                status === "loading"
                  ? "bg-blue-50 border border-blue-200"
                  : status === "success"
                    ? "bg-green-50 border border-green-200"
                    : "bg-red-50 border border-red-200"
              }`}
            >
              {status === "loading" && (
                <Loader className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              )}
              {status === "success" && (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              )}
              {status === "error" && (
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <p
                className={`text-sm ${
                  status === "loading"
                    ? "text-blue-800"
                    : status === "success"
                      ? "text-green-800"
                      : "text-red-800"
                }`}
              >
                {message}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col space-y-4">
            <button
              onClick={handleInitialize}
              disabled={status === "loading" || status === "success"}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "loading"
                ? "Inicializando..."
                : status === "success"
                  ? "✓ Inicializado"
                  : "Inicializar Estructura"}
            </button>

            {status === "success" && (
              <div className="space-y-2">
                <a
                  href="/admin/login"
                  className="block w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 text-center transition-all"
                >
                  Ir al Panel de Admin
                </a>
                <a
                  href="/"
                  className="block w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 text-center transition-all"
                >
                  Ver Portafolio
                </a>
              </div>
            )}

            {status === "idle" && (
              <a
                href="/"
                className="block w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 text-center transition-all"
              >
                Cancelar
              </a>
            )}
          </div>
        </div>

        {/* Footer info */}
        <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Esta página solo se usa una vez para configurar tu base de datos
          </p>
        </div>
      </div>
    </div>
  );
};

export default InitializeData;
